import { useEffect, useRef } from "react";
import useReducedMotion from "@/hooks/useReducedMotion";

/**
 * Parallax vertical ligado al scroll para fondos de héroe.
 *
 * Era el rasgo del lenguaje Apple que faltaba por completo: allí el fondo se
 * mueve más despacio que el contenido y se aleja ligeramente al bajar, lo que
 * da sensación de profundidad. Aquí todo se desplazaba en bloque.
 *
 * Detalles que importan:
 *   • Se escribe sobre el estilo del nodo dentro de requestAnimationFrame, sin
 *     pasar por el estado de React: un re-render por evento de scroll a 60 fps
 *     dejaría la página a tirones.
 *   • Solo se anima `transform`, que la GPU compone sin recalcular layout.
 *   • Se desactiva bajo `prefers-reduced-motion` y en pantallas estrechas: en
 *     móvil el parallax marea y además la barra de direcciones al aparecer y
 *     desaparecer dispara eventos de scroll espurios.
 *
 * @param {number} velocidad  Fracción del scroll que recorre el fondo (0.3 = 30 %).
 */
export default function useParallax(velocidad = 0.3) {
  const ref = useRef(null);
  const reducido = useReducedMotion();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (reducido || window.matchMedia("(max-width: 768px)").matches) {
      el.style.transform = "";
      return;
    }

    let frame = null;

    const actualizar = () => {
      frame = null;
      const rect = el.parentElement?.getBoundingClientRect();
      if (!rect) return;
      // Solo mientras la sección está en pantalla.
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      const desplazamiento = Math.max(0, -rect.top) * velocidad;
      el.style.transform = `translate3d(0, ${desplazamiento}px, 0) scale(1.08)`;
    };

    const onScroll = () => {
      if (frame == null) frame = requestAnimationFrame(actualizar);
    };

    actualizar();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame != null) cancelAnimationFrame(frame);
      el.style.transform = "";
    };
  }, [velocidad, reducido]);

  return ref;
}
